import { Content as EditorFileContent } from '@tiptap/react';
import { AxiosError } from 'axios';
import { NotificationService } from 'services/notification-service';
import { PosthogEvent, PosthogService } from 'services/posthog-service';
import {
  BatchResourceUploadDto,
  Resource,
  ResourceType,
  UnUploadedResource,
  UpdateResourceFileDto,
  UpdateResourceInfoDto,
  UploadResourceEventType,
  UploadResourceFinalEvent,
  UploadResourceProgressEvent
} from 'shared/interfaces/Resource';
import { axiosBaseInstance } from 'shared/utils/axios-instances';
import { getEvents, getLatestEvent } from 'shared/utils/http-streaming';

const resourceBaseEndpoint = '/resource';

export const ResourceService = {
  getAllResourcesCreatedByMe: async (): Promise<Resource[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/created-by-me`
    });

    return response.data;
  },

  getAllResourcesByTeam: async (teamId: string): Promise<Resource[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/team/${teamId}`
    });

    return response.data;
  },

  getAllTextResourcesCreatedByMe: async (): Promise<Resource[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/created-by-me`
    });

    const resources = await response.data;
    const textResources = resources.filter((resource: Resource) => resource.type === ResourceType.TEXT);

    return textResources;
  },

  batchUploadResource: async (batchResourceUploadDto: BatchResourceUploadDto): Promise<Resource[]> => {
    const batchUploadProgressEventMap = new Map<string, UploadResourceProgressEvent>();

    for (const resource of batchResourceUploadDto.resources) {
      batchUploadProgressEventMap.set(resource.fileName, {
        resourceFileName: resource.fileName,
        resourceType: resource.type,
        progress: 0,
        type: UploadResourceEventType.PROGRESS
      } as UploadResourceProgressEvent);
    }

    NotificationService.batchUploadProgression(
      Array.from(batchUploadProgressEventMap.values()),
      batchResourceUploadDto.notificationId,
      batchResourceUploadDto.notificationTitleKey
    );

    const uploads = [];
    for (const resource of batchResourceUploadDto.resources) {
      if (resource.fileWithPath) {
        const formData = new FormData();
        formData.append('file', resource.fileWithPath);
        uploads.push(
          ResourceService.uploadResource(
            formData,
            resource,
            batchUploadProgressEventMap,
            batchResourceUploadDto.addResources,
            batchResourceUploadDto.notificationId,
            batchResourceUploadDto.notificationTitleKey
          )
        );
      }

      if (resource.youtubeVideo) {
        const formData = new FormData();
        formData.append('youtubeVideoId', resource.youtubeVideo.id);
        uploads.push(
          ResourceService.uploadResource(
            formData,
            resource,
            batchUploadProgressEventMap,
            batchResourceUploadDto.addResources,
            batchResourceUploadDto.notificationId,
            batchResourceUploadDto.notificationTitleKey
          )
        );
      }

      if (resource.googleDriveFile) {
        const formData = new FormData();
        formData.append(
          'googleDrive',
          JSON.stringify({
            fileId: resource.googleDriveFile.id,
            googleDriveAccountId: resource.googleDriveFile.googleDriveAccountId
          })
        );
        if (resource.syncFrequency) formData.append('syncFrequency', JSON.stringify(resource.syncFrequency));
        uploads.push(
          ResourceService.uploadResource(
            formData,
            resource,
            batchUploadProgressEventMap,
            batchResourceUploadDto.addResources,
            batchResourceUploadDto.notificationId,
            batchResourceUploadDto.notificationTitleKey
          )
        );
      }

      if (resource.website) {
        const formData = new FormData();
        resource.website.subLinks = resource.website.subLinks.filter((sublink) => sublink !== resource.website?.url);
        formData.append('website', JSON.stringify(resource.website));
        if (resource.syncFrequency) formData.append('syncFrequency', JSON.stringify(resource.syncFrequency));
        uploads.push(
          ResourceService.uploadResource(
            formData,
            resource,
            batchUploadProgressEventMap,
            batchResourceUploadDto.addResources,
            batchResourceUploadDto.notificationId,
            batchResourceUploadDto.notificationTitleKey
          )
        );
      }

      if (resource.shopifyStoreId) {
        const formData = new FormData();
        formData.append('shopifyStoreId', resource.shopifyStoreId);
        uploads.push(
          ResourceService.uploadResource(
            formData,
            resource,
            batchUploadProgressEventMap,
            batchResourceUploadDto.addResources,
            batchResourceUploadDto.notificationId,
            batchResourceUploadDto.notificationTitleKey
          )
        );
      }
    }

    const uploadedResources = (await Promise.all(uploads)).filter((resource) => resource !== undefined) as Resource[];
    // Capture the created resources in PostHog
    ResourceService.captureCreatedResources(uploadedResources);

    return uploadedResources;
  },

  uploadResource: async (
    data: FormData,
    unUploadedResource: UnUploadedResource,
    batchUploadProgressEventMap: Map<string, UploadResourceProgressEvent>,
    addResources?: (resources: Resource[], override: boolean) => void,
    notificationId?: string,
    notificationTitleKey?: string
  ): Promise<Resource | undefined> => {
    // Process common attributes from the unUploadedResource
    if (unUploadedResource.tags) {
      for (const tag of unUploadedResource.tags) {
        data.append('tags[]', tag);
      }
    }

    const response = await axiosBaseInstance({
      method: 'post',
      url: `${resourceBaseEndpoint}/upload-resource`,
      data: data,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && progressEvent.total !== 0) {
          let serverUploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);

          // We divide by 2 because we have 2 steps: server upload and storage upload
          if (data.has('file')) {
            serverUploadPercentage /= 2;
          }

          // Create a new event with the new upload progress to server
          const serverUploadEvent = {
            resourceFileName: unUploadedResource.fileName,
            resourceType: unUploadedResource.type,
            progress: serverUploadPercentage,
            type: UploadResourceEventType.PROGRESS
          } as UploadResourceProgressEvent;

          batchUploadProgressEventMap.set(unUploadedResource.fileName, serverUploadEvent);

          // Notyify the progress
          NotificationService.batchUploadProgression(
            Array.from(batchUploadProgressEventMap.values()),
            notificationId,
            notificationTitleKey
          );
        }
      },
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onDownloadProgress(progressEvent) {
        let newProgress = false;
        const events = getEvents(progressEvent.event?.currentTarget?.response);
        for (const event of events) {
          const batchUploadEvent = JSON.parse(event);
          if (batchUploadEvent.type === UploadResourceEventType.PROGRESS) {
            if (!newProgress) newProgress = true;

            // Set the storage upload percentage
            const storageUploadPercentage = 50 + batchUploadEvent.progress / 2;
            batchUploadEvent.progress = storageUploadPercentage;

            // key is file name because we might not receive the id yet.
            // ie: when progress starts, the resource has not been added to the database yet
            batchUploadProgressEventMap.set(batchUploadEvent.resourceFileName, batchUploadEvent);
          } else if (batchUploadEvent.type === UploadResourceEventType.ERROR) {
            const lastProgressEvent = batchUploadProgressEventMap.get(unUploadedResource.fileName);

            if (lastProgressEvent) {
              batchUploadProgressEventMap.set(unUploadedResource.fileName, {
                ...lastProgressEvent,
                failed: true
              });
              NotificationService.batchUploadProgression(
                Array.from(batchUploadProgressEventMap.values()),
                notificationId,
                notificationTitleKey
              );
            }
          }
        }

        if (newProgress)
          NotificationService.batchUploadProgression(
            Array.from(batchUploadProgressEventMap.values()),
            notificationId,
            notificationTitleKey
          );
      }
    });
    const lastEvent = JSON.parse(getLatestEvent(response.data as string));

    if (lastEvent.type === UploadResourceEventType.ERROR) {
      const error = new AxiosError(undefined, lastEvent.error.statusCode, undefined, undefined, {
        status: lastEvent.error.statusCode,
        statusText: '',
        data: lastEvent.error,
        headers: response.headers,
        config: response.config
      });
      NotificationService.endAction(
        undefined,
        `${lastEvent.error.message}`,
        'FEED_BACK.RESOURCES.FAILURE.BATCH_UPLOAD.SINGLE_GENERIC',
        error
      );
    }

    if (lastEvent.type === UploadResourceEventType.FINAL) {
      const resource = (lastEvent as UploadResourceFinalEvent).resource;
      addResources ? addResources([resource], true) : null;
      return resource;
    }
  },

  syncResource: async (resourceId: string): Promise<Resource> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${resourceBaseEndpoint}/sync/${resourceId}`
    });

    return response.data;
  },

  deleteResource: async (resourceId: string): Promise<string> => {
    const response = await axiosBaseInstance({
      method: 'delete',
      url: `${resourceBaseEndpoint}/${resourceId}`
    });

    return response.data._id;
  },

  updateResourceFile: async (updateResourceDto: UpdateResourceFileDto): Promise<Resource> => {
    const formdata = new FormData();
    if (updateResourceDto.fileWithPath) {
      formdata.append('file', updateResourceDto.fileWithPath);
    }

    const response = await axiosBaseInstance({
      method: 'put',
      url: `${resourceBaseEndpoint}/file/${updateResourceDto.resourceId}`,
      data: formdata,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  updateResourceInfo: async (updateResourceInfoDto: UpdateResourceInfoDto): Promise<Resource> => {
    const response = await axiosBaseInstance({
      method: 'put',
      url: `${resourceBaseEndpoint}/info/${updateResourceInfoDto.resourceId}`,
      data: updateResourceInfoDto
    });

    return response.data;
  },

  getResourceInfo: async (resourceId: string): Promise<Resource> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/info/${resourceId}`
    });

    return response.data;
  },

  downloadResourceFile: async (resourceId: string): Promise<ArrayBuffer> => {
    const response = await axiosBaseInstance({
      method: 'get',
      responseType: 'arraybuffer',
      url: `${resourceBaseEndpoint}/download-file/${resourceId}`
    });

    return response.data;
  },

  getVideoSignedUrl: async (resourceId: string): Promise<string> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/video/${resourceId}`
    });
    return response.data;
  },

  downloadResourceTextFile: async (resourceId: string): Promise<EditorFileContent> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/download-file/${resourceId}`
    });

    return response.data;
  },

  createTag: async (tag: string, resourceIds: string[]): Promise<Resource[]> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${resourceBaseEndpoint}/tag`,
      data: { tag, resourceIds }
    });

    return response.data;
  },

  getResourceByShopifyStoreId: async (shopifyStoreId: string): Promise<Resource | undefined> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/shopify-resource-store/${shopifyStoreId}`
    });

    return response.data;
  },

  getResourceRawText: async (resourceId: string): Promise<string> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${resourceBaseEndpoint}/raw-text/${resourceId}`
    });

    return response.data;
  },
  captureCreatedResources: (resources: Resource[]) => {
    for (const resource of resources) {
      PosthogService.capture(PosthogEvent.RESOURCE_CREATED, resource);
    }
  }
};
